import { useEffect, useState } from 'react'

const TOKEN_ENDPOINT = '/api/v2/mapkit/token'
const REFRESH_LEAD_MS = 60_000

export interface MapKitToken {
  token: string
  expiresAt: string
}

interface CachedToken {
  token: string
  expiresAt: string
  expiresAtMs: number
}

let cached: CachedToken | null = null
let inflight: Promise<CachedToken> | null = null

async function fetchToken(): Promise<CachedToken> {
  const res = await fetch(TOKEN_ENDPOINT)
  if (!res.ok) {
    throw new Error(`mapkit token: HTTP ${res.status}`)
  }
  const body = (await res.json()) as MapKitToken
  return {
    token: body.token,
    expiresAt: body.expiresAt,
    expiresAtMs: Date.parse(body.expiresAt),
  }
}

function isFresh(entry: CachedToken | null, nowMs: number): entry is CachedToken {
  return entry !== null && entry.expiresAtMs - REFRESH_LEAD_MS > nowMs
}

async function getToken(): Promise<CachedToken> {
  const now = Date.now()
  if (isFresh(cached, now)) {
    return cached
  }
  if (inflight) {
    return inflight
  }
  inflight = fetchToken()
    .then((entry) => {
      cached = entry
      return entry
    })
    .finally(() => {
      inflight = null
    })
  return inflight
}

export function useMapKitToken(): MapKitToken | null {
  const [token, setToken] = useState<MapKitToken | null>(() =>
    isFresh(cached, Date.now()) ? { token: cached.token, expiresAt: cached.expiresAt } : null,
  )

  useEffect(() => {
    let cancelled = false
    getToken()
      .then((entry) => {
        if (!cancelled) {
          setToken({ token: entry.token, expiresAt: entry.expiresAt })
        }
      })
      .catch(() => {
        // Swallow; placeholder remains visible. Production errors are
        // observable via network inspector — no UX-facing surfacing yet.
      })
    return (): void => {
      cancelled = true
    }
  }, [])

  return token
}

export function __resetMapKitTokenCacheForTests(): void {
  cached = null
  inflight = null
}
